import { CredentialField } from './CredentialField'
import { useEffect, useState, type FormEvent } from 'react'
import { Button, Icon, toastSuccess } from 'admination-react'
import {
  registerWithEmail,
  requestPasswordReset,
  signInWithPassword,
} from '@/core/api/auth'
import { ApiError, setActiveOrganisationId } from '@/core/api/http'
import { resetMe } from '@/core/data/session'
import { useAuth } from '@/core/state/auth'
import {
  EMAIL_INVALID,
  LOGIN_FAILED,
  LOGIN_INVALID,
  LOGIN_MISSING,
  REGISTERED_NOTICE,
  REGISTER_SENT,
  REQUEST_FAILED,
  RESET_REQUESTED,
  looksLikeEmail,
  tooManyAttemptsMessage,
} from './messages'

type View = 'login' | 'register' | 'vergessen'

type ActiveAccount = { email: string; onContinue: () => void }

export function Login({ registered = false, activeAccount }: { registered?: boolean; activeAccount?: ActiveAccount }) {
  const [view, setView] = useState<View>('login')
  useEffect(() => {
    if (registered) toastSuccess(REGISTERED_NOTICE)
  }, [registered])

  return (
    <div className="page" id="page">
      <div className="layout layout--center" id="base-layout">
        <main className="form form--narrow">
          {(view === 'login' || (activeAccount && view === 'register')) && <SignIn registered={registered} authenticatedEmail={activeAccount?.email}
            onNavigate={(next) => { if (activeAccount && next === 'register') activeAccount.onContinue(); else setView(next) }}
          />}
          {!activeAccount && view === 'register' && (
            <EmailRequest
              key="register"
              heading="Registrieren"
              intro="Geben Sie Ihre dienstliche E-Mail-Adresse ein."
              submitLabel="Registrieren"
              sentMessage={REGISTER_SENT}
              send={registerWithEmail}
              onBack={() => setView('login')}
            />
          )}
          {view === 'vergessen' && (
            <EmailRequest
              key="vergessen"
              heading="Passwort vergessen"
              intro="Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen."
              submitLabel="Link anfordern"
              sentMessage={RESET_REQUESTED}
              send={requestPasswordReset}
              onBack={() => setView('login')}
            />
          )}
        </main>
      </div>
    </div>
  )
}

function SignIn({ registered, onNavigate, authenticatedEmail }: { registered: boolean; onNavigate: (view: View) => void; authenticatedEmail?: string }) {
  const { refresh } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState(authenticatedEmail ?? '')
  const [password, setPassword] = useState('')

  async function submitPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return
    if (authenticatedEmail) { onNavigate('register'); return }
    if (!email.trim() || !password) {
      setError(LOGIN_MISSING)
      return
    }
    setBusy(true)
    setError(null)
    try {
      setActiveOrganisationId(null)
      await signInWithPassword(email.trim(), password)
    } catch (err) {
      setPassword('')
      const tooMany = tooManyAttemptsMessage(err)
      setError(
        err instanceof ApiError && err.status === 401
          ? LOGIN_INVALID
          : (tooMany ?? LOGIN_FAILED),
      )
      setBusy(false)
      return
    }
    resetMe()
    try {
      await refresh()
    } catch (error) { void error }
    setError(LOGIN_FAILED)
    setBusy(false)
  }

  return (
    <div className="section">
      <h3 className="section-header">Anmelden</h3>
      <div className="section-body">
        {registered ? (
          <p role="status" className="section-note">
            {REGISTERED_NOTICE}
          </p>
        ) : (
          <p className="section-note">Melden Sie sich an, um fortzufahren.</p>
        )}
        <form
          onSubmit={(e) => void submitPassword(e)}
          noValidate
          aria-label="Mit E-Mail-Adresse anmelden"
          className="section-body"
        >
          <CredentialField label="E-Mail-Adresse" id="login-email" name="email" type="email"
            autoComplete="username" value={email} onChange={setEmail}
            disabled={busy} readOnly={!!authenticatedEmail} />
          <CredentialField label="Passwort" id="login-password" name="password" type="password"
            autoComplete="current-password" value={password} onChange={setPassword}
            disabled={busy} />
          <Button type="submit" variant="primary" block loading={busy}>
            <Icon name="i-arrow-right" />Anmelden</Button>
        </form>

        {error && (
          <p role="alert" className="field-error">
            {error}
          </p>
        )}

        <div className="section-actions">
          <Button onClick={() => onNavigate('register')} disabled={busy}>
            Registrieren</Button>
          <Button onClick={() => onNavigate('vergessen')} disabled={busy}>
            Passwort vergessen?</Button>
        </div>
      </div>
    </div>
  )
}

function EmailRequest({
  heading,
  intro,
  submitLabel,
  sentMessage,
  send,
  onBack,
}: {
  heading: string
  intro: string
  submitLabel: string
  sentMessage: string
  send: (email: string) => Promise<void>
  onBack: () => void
}) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return
    if (!looksLikeEmail(email)) {
      setError(EMAIL_INVALID)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await send(email.trim())
      setSent(true)
    } catch (err) {
      setError(tooManyAttemptsMessage(err) ?? REQUEST_FAILED)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="section">
      <h3 className="section-header">{heading}</h3>
      <div className="section-body">
        {sent ? (
          <p role="status">
            {sentMessage}
          </p>
        ) : (
          <>
            <p className="section-note">{intro}</p>
            <form onSubmit={(e) => void submit(e)} noValidate className="section-body">
              <CredentialField label="E-Mail-Adresse" id="request-email" name="email" type="email"
                autoComplete="username" value={email} onChange={setEmail}
                disabled={busy} />
              <Button type="submit" variant="primary" block loading={busy}>
                <Icon name="i-send" />{submitLabel}
              </Button>
            </form>
            {error && (
              <p role="alert" className="field-error">
                {error}
              </p>
            )}
          </>
        )}
        <Button onClick={onBack} disabled={busy}>
          Zurück zur Anmeldung</Button>
      </div>
    </div>
  )
}
